/**
 * Which order statuses mean "done with", for the purpose of loading a truck.
 *
 * A truck carrying several orders is loaded one order at a time — the pallets
 * go on in that sequence and must not be mixed — so an order can only be
 * worked once every order ahead of it on the trip is finished.
 *
 * Cancelled counts as finished. It will never be loaded, and treating it as
 * unfinished would strand every order behind it on the truck. (A cancellation
 * that happens with pallets ALREADY loaded needs an unload process that does
 * not exist yet; this says nothing about that case.)
 *
 * The API enforces the same rule server-side — `OrderStatusRules.Finished` in
 * `DockAutomation.Data/Enum/OrderStatusRules.cs`. This copy exists so the
 * screen can explain itself before the operator scans, not to replace it: the
 * server is the guarantee, this is the courtesy.
 */
const FINISHED = new Set(['warehousecheckedout', 'officecheckedout', 'cancelled'])

/**
 * A status as words for the screen: the server's enum name spaced out, so
 * `WarehouseCheckedIn` reads "Warehouse Checked In", the name the office
 * screens use. Never show the enum name itself.
 */
export function orderStatusWords(status: string): string {
  return status.trim().replace(/([a-z])([A-Z])/g, '$1 $2')
}

/** Status names arrive as the server's enum names, e.g. `WarehouseCheckedOut`. */
export function orderIsFinished(status: string): boolean {
  return FINISHED.has(status.trim().toLowerCase())
}

/**
 * Whether an order ahead on the truck lets the next one start (ID30 decision
 * 0003): finished with, or every pallet it asks for aboard. The office's
 * check-out is a claim about the truck made afterwards; a driver at the door
 * does not wait for it. Counts not known yet (null) cannot say "full", so only
 * a finished status releases until they land. The API judges the same way, per
 * product; this is the courtesy that lets the screen explain itself.
 */
export function orderReleasesNext(
  status: string,
  palletsLoaded: number | null | undefined,
  palletsRequired: number | null | undefined,
): boolean {
  if (orderIsFinished(status)) return true
  if (palletsRequired == null || palletsRequired <= 0) return false
  return (palletsLoaded ?? 0) >= palletsRequired
}
